import {
  Building2,
  HeartHandshake,
  Stethoscope,
  Users,
  Plane,
  ShoppingBasket,
  PackageCheck,
  TestTube,
  Route,
  ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "../lib/motion";
import { scrollToId } from "../lib/smoothScroll";
import { btnPrimary } from "../lib/contact";

const SERVICES = [
  {
    icon: Building2,
    title: "Admissions, Discharges and Transfers",
    body: "Transportation to and from detox, residential inpatient treatment, PHP, IOP, outpatient programs, sober living residences, and halfway houses.",
  },
  {
    icon: HeartHandshake,
    title: "MAT and Therapy Appointments",
    body: "Daily, weekly, or monthly transportation to medication-assisted treatment, counseling, therapy, and other recovery-related appointments.",
  },
  {
    icon: Stethoscope,
    title: "Medical Appointments",
    body: "Transportation for inpatient clients, program participants, and other passengers who need to attend outside doctors' appointments, therapy, or other scheduled healthcare visits.",
  },
  {
    icon: Users,
    title: "Recovery Meetings",
    body: "Transportation to and from AA, NA, and other peer-support or recovery meetings.",
  },
  {
    icon: Plane,
    title: "Airport and Station Transportation",
    body: "Pickup and drop-off service for airports, train stations, and bus stations, including transportation connected to treatment admission, discharge, or relocation.",
  },
  {
    icon: ShoppingBasket,
    title: "Grocery and Essential Shopping Trips",
    body: "Scheduled transportation for sober living residences, halfway houses, PHP programs, IOP programs, treatment facilities, individuals, and families needing grocery or essential-supply trips.",
  },
  {
    icon: PackageCheck,
    title: "Store Trips and Concierge Pickup",
    body: "When staff members are stretched thin, Recovery Rides can pick up needed items from grocery stores, pharmacies, and other local businesses. Designed for programs and residences, but available to anyone.",
  },
  {
    icon: TestTube,
    title: "Laboratory Deliveries",
    body: "Dependable delivery of urine-analysis samples and other authorized materials to the appropriate laboratory.",
  },
  {
    icon: Route,
    title: "Long-Distance Transportation",
    body: "Local and long-distance transportation is available. There is no set maximum trip distance. Rates are determined by flat per-mile pricing tiers.",
  },
];

export const Services = () => (
  <section id="services" className="relative bg-white py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-center">
        <div className="max-w-2xl">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#0891B2] mb-4">
              Our Services
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#3B3A4E] leading-tight">
              Concierge Transportation for Every Stage of Recovery
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-5 text-base sm:text-lg text-[#5C5975] leading-relaxed">
              Recovery Rides provides transportation and concierge support across all levels of care. Services
              are available to treatment providers, recovery organizations, individuals, families, and anyone
              needing dependable transportation.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="relative rounded-[2rem] rounded-br-[3.5rem] overflow-hidden shadow-xl shadow-[#3B3A4E]/12 aspect-[3/2] bg-[#F5F4F1]">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/dc8a482d-361d-4996-9eb5-a4cbac9ec941/images/d68676bbc87a072248c09737e2216fe82ba0323a67328d9a6fecf30462e9fd57.jpeg"
              alt="Illustration of Michael, the Recovery Rides host, presenting the range of concierge transportation services"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </Reveal>
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {SERVICES.map((s, i) => (
          <motion.article
            key={s.title}
            variants={item}
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 300, damping: 22 }}
            className="group relative rounded-3xl bg-[#FAFAF9] border border-[#3B3A4E]/8 p-7 overflow-hidden transition-shadow duration-300 hover:shadow-xl hover:shadow-[#3B3A4E]/10"
            data-testid={`service-card-${i}`}
          >
            <div className="pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full bg-[#1AD1B9]/0 group-hover:bg-[#1AD1B9]/12 blur-2xl transition-all duration-500" />
            <div className="relative">
              <div className="h-13 w-13 rounded-2xl brand-gradient flex items-center justify-center mb-5 shadow-md shadow-teal-500/20" style={{ height: "3.25rem", width: "3.25rem" }}>
                <s.icon size={24} className="text-white" strokeWidth={2} />
              </div>
              <span className="text-xs font-mono font-semibold text-[#8B88A5]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-1 text-lg font-heading font-semibold text-[#2D2B3D] leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C5975] leading-relaxed">{s.body}</p>
            </div>
          </motion.article>
        ))}
      </motion.div>

      <Reveal delay={0.1} className="mt-12 flex justify-center">
        <button onClick={() => scrollToId("contact")} className={btnPrimary} data-testid="services-request-service">
          Request Service <ArrowRight size={18} />
        </button>
      </Reveal>
    </div>
  </section>
);
